/**
 * Tool Picker component for Arrange phase in registry-demo.
 * 
 * ============================================================================
 * INVARIANTS (Doc 06, Doc 08) - Batch 3
 * ============================================================================
 * 
 * 1. Tool availability is determined by server-side evaluation only.
 * 2. UI MUST NOT compute or infer tool availability.
 * 3. Blocked tools show diagnostic cards, not disabled buttons (Doc 08).
 * 4. Each blocker includes remediation routes for resolution.
 */

import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  AlertTriangle, 
  CheckCircle, 
  ExternalLink, 
  Loader2,
  RefreshCw,
  Lock,
  Sparkles,
} from 'lucide-react';
import { useArrangeStore } from '../../stores/prioritization/arrangeStore';
import { getAllAdapters, getAdaptersByCategory } from "../../harmonizer";
import { useDegapStore } from '../../stores/prioritization/degapStore';
import { 
  useToolReadinessStore, 
  useToolsReadiness,
  type ToolBlocker,
} from '../../stores/prioritization/toolReadinessStore';
import {
  getRecommendationsForConfiguration,
  type ObjectType,
  type OutputShape,
} from '../../lib/toolRecommendationMatrix';
import { ContextualReadinessPanel } from './ContextualReadinessPanel';
import { useWorkspaceModules, isToolEnabledByAdapterName, recommendedToolsAvailable } from '../../lib/workspaceModulesService';

// ============================================================================
// Blocked Tool Card Component
// ============================================================================

interface BlockedToolCardProps {
  toolName: string;
  blockers: ToolBlocker[];
  requiredAttributes?: string[];
}

function BlockedToolCard({ toolName, blockers }: BlockedToolCardProps) {
  return (
    <div className="p-4 border border-[var(--mustard)] bg-[var(--mustard-dim)]">
      {/* Tool header with blocked indicator */}
      <div className="flex items-start gap-2 mb-3">
        <Lock className="h-4 w-4 text-[var(--mustard)] mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <div className="font-medium text-[var(--mustard)]">{toolName}</div>
          <div className="text-xs text-[var(--mustard)] mt-0.5">
            {blockers.length} prerequisite{blockers.length !== 1 ? 's' : ''} not met
          </div>
        </div>
      </div>
      
      {/* Blockers list */}
      <div className="space-y-2">
        {blockers.map((blocker, index) => (
          <div 
            key={blocker.code || index}
            className="flex items-start gap-2 p-2 bg-[var(--bg-raised)] rounded border border-[var(--mustard)]"
          >
            <AlertTriangle className="h-3.5 w-3.5 text-[var(--mustard)] mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-sm text-[var(--text-pri)]">{blocker.title}</div>
              <div className="text-xs text-[var(--text-ter)] mt-0.5">
                {blocker.why_it_matters}
              </div>
              <div className="text-xs text-[var(--text-ter)] mt-0.5">
                Current: {blocker.current_state}
              </div>
            </div>
            {/* Remediation link - PRIMARY CTA per Doc 08 */}
            {blocker.remediation_route && (
              <Link
                to={blocker.remediation_route}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-[var(--text-sec)] bg-[var(--slate-dim)] rounded hover:bg-[var(--slate-dim)] transition-colors flex-shrink-0"
              >
                Fix
                <ExternalLink className="h-3 w-3" />
              </Link>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// Available Tool Card Component
// ============================================================================

interface AvailableToolCardProps {
  toolName: string;
  isSelected: boolean;
  requiredAttributes?: string[];
  onSelect: () => void;
}

function AvailableToolCard({ 
  toolName, 
  isSelected, 
  requiredAttributes,
  onSelect,
}: AvailableToolCardProps) {
  return (
    <button
      onClick={onSelect}
      className={`
        p-4 border  text-left transition-colors
        ${isSelected
          ? "border-[var(--slate)] bg-[var(--slate-dim)]"
          : "border-[var(--rule)] hover:border-[var(--rule)] hover:bg-[var(--surface)]"
        }
      `}
    >
      <div className="flex items-start gap-2">
        <CheckCircle className="h-4 w-4 text-[var(--green)] mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <div className="font-medium">{toolName}</div>
          {requiredAttributes && requiredAttributes.length > 0 && (
            <div className="text-xs text-[var(--text-ter)] mt-1">
              Requires: {requiredAttributes.join(", ")}
            </div>
          )}
        </div>
      </div>
    </button>
  );
}

// ============================================================================
// Recommended Tool Card Component
// ============================================================================

interface RecommendedToolCardProps {
  toolId: string;
  toolName: string;
  rationale: string;
  isSelected: boolean;
  onSelect: () => void;
}

function RecommendedToolCard({ toolName, rationale, isSelected, onSelect }: RecommendedToolCardProps) {
  return (
    <button
      onClick={onSelect}
      className={`
        p-4 border  text-left transition-colors w-full
        ${isSelected
          ? 'border-primary-500 bg-primary-50'
          : 'border-primary-200 bg-[var(--bg-raised)] hover:border-primary-300 hover:bg-primary-50/50'
        }
      `}
    >
      <div className="flex items-start gap-2">
        <Sparkles className="h-4 w-4 text-primary-500 mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <div className="font-medium text-[var(--text-pri)]">{toolName}</div>
          <div className="text-xs text-[var(--text-ter)] mt-0.5">{rationale}</div>
        </div>
      </div>
    </button>
  );
}

// ============================================================================
// Main Component
// ============================================================================

interface ToolPickerProps {
  /** Object types declared by the active configuration (from configurations.object_types) */
  configurationObjectTypes?: ObjectType[];
  /** Output shapes declared by the active configuration (from configurations.output_shapes) */
  configurationOutputShapes?: OutputShape[];
  /** Cycle ID for tool availability evaluation (legacy usage from Arrange/Exercise pages) */
  cycleId?: string;
  /** Skip the phase gate check (for exercises and deprecated Arrange page) */
  skipPhaseGate?: boolean;
}

export function ToolPicker({
  configurationObjectTypes,
  configurationOutputShapes,
}: ToolPickerProps = {}) {
  const { selectedTool, setSelectedTool } = useArrangeStore();
  const { canRunTools, currentCycle } = useDegapStore();
  const workspaceFeatures = useWorkspaceModules();
  
  // Get tool readiness from server-authoritative store
  const evaluateAllTools = useToolReadinessStore(state => state.evaluateAllTools);
  const isEvaluating = useToolReadinessStore(state => state.isEvaluating);
  // Get cycle ID for evaluation
  const cycleId = currentCycle?.id;
  
  // Evaluate tools on mount and when cycle changes
  useEffect(() => {
    if (cycleId && canRunTools()) {
      evaluateAllTools(cycleId);
    }
  }, [cycleId, canRunTools, evaluateAllTools]);
  
  // Get readiness data using the hook
  const { availableTools, blockedTools, isLoading } = useToolsReadiness(cycleId ?? '');
  
  const adapters = getAllAdapters().filter(
    (a) => isToolEnabledByAdapterName(workspaceFeatures, a.toolName)
  );
  const filterEnabled = (list: ReturnType<typeof getAllAdapters>) =>
    list.filter((a) => isToolEnabledByAdapterName(workspaceFeatures, a.toolName));
  const toolsByCategory = {
    scoring: filterEnabled(getAdaptersByCategory("scoring_model")),
    categorization: filterEnabled(getAdaptersByCategory("categorization_model")),
    voting: filterEnabled(getAdaptersByCategory("voting_model")),
    comparison: filterEnabled(getAdaptersByCategory("comparison_model")),
  };

  // Compute matrix-based recommendations when configuration metadata is present
  const matrixRecs = (configurationObjectTypes?.length && configurationOutputShapes?.length)
    ? getRecommendationsForConfiguration(configurationObjectTypes, configurationOutputShapes)
    : null;

  // Only surface recommendations for tools that are both available (not blocked) and enabled by
  // workspace module. Readiness is keyed by adapter name, the matrix by tool id — pair them
  // through the module table, never by comparing the strings.
  const recommendedAvailable = matrixRecs
    ? recommendedToolsAvailable(matrixRecs.primaryToolIds, availableTools, workspaceFeatures)
    : [];

  // Phase gate check
  if (!canRunTools()) {
    return (
      <div className="p-4 bg-[var(--mustard-dim)] border border-[var(--mustard)]">
        <p className="text-sm text-[var(--mustard)]">
          Tools can only be run in the ARRANGE phase. Please transition to ARRANGE first.
        </p>
      </div>
    );
  }
  
  // Loading state
  if (isLoading || isEvaluating(cycleId ?? '')) {
    return (
      <div className="p-4 bg-[var(--surface)] border border-[var(--rule)]">
        <div className="flex items-center gap-2 text-[var(--text-ter)]">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span className="text-sm">Evaluating tool availability...</span>
        </div>
      </div>
    );
  }

  // Helper to check if a tool is available
  const isToolAvailable = (toolName: string) => {
    return availableTools.includes(toolName);
  };
  
  // Helper to get blockers for a tool
  const getBlockersForTool = (toolName: string): ToolBlocker[] => {
    const blocked = blockedTools.find(t => t.toolName === toolName);
    return blocked?.blockers ?? [];
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">Select a Prioritization Tool</h2>
        {cycleId && (
          <button
            onClick={() => evaluateAllTools(cycleId)}
            className="p-1.5 text-[var(--text-ter)] hover:text-[var(--text-sec)] hover:bg-[var(--hover-tint)] rounded"
            title="Re-evaluate tool availability"
          >
            <RefreshCw className="h-4 w-4" />
          </button>
        )}
      </div>
      
      {/* Summary of available/blocked */}
      <div className="flex items-center gap-4 text-sm">
        <span className="inline-flex items-center gap-1 text-[var(--green)] dark:text-[var(--text-ter)]">
          <CheckCircle className="h-4 w-4" />
          {availableTools.length} available
        </span>
        {blockedTools.length > 0 && (
          <span className="inline-flex items-center gap-1 text-[var(--mustard)]">
            <Lock className="h-4 w-4" />
            {blockedTools.length} blocked
          </span>
        )}
      </div>

      {/* Hierarchy context — shows what needs to be fixed at cycle/track level */}
      {blockedTools.length > 0 && cycleId && (
        <ContextualReadinessPanel
          nodeType="cycle"
          nodeId={cycleId}
          collapseWhenReady
        />
      )}

      {/* Recommended for this configuration (Thing 2B) */}
      {recommendedAvailable.length > 0 && matrixRecs && (
        <div className="mb-2">
          <h3 className="text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wide mb-3">
            Recommended for this configuration
          </h3>
          <div className="space-y-2">
            {recommendedAvailable.map(({ toolId, adapterName }) => {
              const rec = matrixRecs.byToolId[toolId];
              if (!rec) return null;
              return (
                <RecommendedToolCard
                  key={toolId}
                  toolId={toolId}
                  toolName={rec.primaryToolName}
                  rationale={rec.rationale}
                  isSelected={selectedTool === adapterName}
                  onSelect={() => setSelectedTool(adapterName)}
                />
              );
            })}
          </div>
          <div className="border-t border-[var(--rule)] mt-4 pt-4">
            <h3 className="text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wide mb-3">
              All compatible tools
            </h3>
          </div>
        </div>
      )}

      {Object.entries(toolsByCategory).map(([category, tools]) => {
        if (tools.length === 0) return null;
        
        return (
          <div key={category} className="space-y-2">
            <h3 className="text-sm font-medium text-[var(--text-sec)] uppercase tracking-wide">
              {category.replace("_", " ")}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {tools.map((adapter) => {
                const toolAvailable = isToolAvailable(adapter.toolName);
                const toolBlockers = getBlockersForTool(adapter.toolName);
                
                if (toolAvailable) {
                  return (
                    <AvailableToolCard
                      key={adapter.toolName}
                      toolName={adapter.toolName}
                      isSelected={selectedTool === adapter.toolName}
                      requiredAttributes={adapter.inputContract.requiredAttributes}
                      onSelect={() => setSelectedTool(adapter.toolName)}
                    />
                  );
                } else {
                  // Render blocked tool with diagnostics (Doc 08)
                  return (
                    <BlockedToolCard
                      key={adapter.toolName}
                      toolName={adapter.toolName}
                      blockers={toolBlockers}
                      requiredAttributes={adapter.inputContract.requiredAttributes}
                    />
                  );
                }
              })}
            </div>
          </div>
        );
      })}

      {adapters.length === 0 && (
        <div className="p-4 bg-[var(--surface)] border border-[var(--rule)]">
          <p className="text-sm text-[var(--text-sec)]">
            No tools available. Ensure the harmonizer registry is initialized.
          </p>
        </div>
      )}
    </div>
  );
}


